import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { FieldGroupAccordion } from '@components/ui/field-group-accordion';
import { SimpleTooltip } from '@components/ui/tooltip';
import { CaseState } from '@constants/caseStates';
import { useUpdateCaseInput, useUpdateCaseOutput } from '@hooks/useCases';
import { useManualTaskStarted } from '@hooks/useManualTaskStarted';
import {
  useIsCaseProgramAdmin,
  useIsCurrentUserAssigned,
  useIsTeamLead,
} from '@hooks/usePermissions';
import { useCalculateDisposition, useCaseSummary, useRegenerateSummary } from '@hooks/useSummary';
import { useStore } from '@store/index';
import { createAllAccordionIndices, toggleAllAccordionItems } from '@utils/accordion';
import { formatErrorMessage } from '@utils/errorMessageFormatter';
import { coerceFormDataToSchema, constructEmptyDataFromGroups } from '@utils/formSchema';
import { normalizePhoneFieldsForSave } from '@utils/phone';
import { ChevronsDownUp, CircleAlert, Edit3, Loader, RefreshCw, Save, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { MoveToMedBV } from '@/components/MoveToMedBV';
import { NotesColumn } from '@/components/NotesColumn';
import { OutputBoardEditor } from '@/components/OutputBoardEditor';
import { SOBPreview } from '@/components/SOBPreview';
import { TasksColumn } from '@/components/TasksColumn';
import { isMyTasksEnabledForOrg } from '@/constants/feature-flags';
import { useOutputEditor } from '@/hooks/useOutputEditor';
import { FaxSummaryView } from '@/pages/Fax/FaxSummaryView';
import { useFax } from '@/pages/Fax/useFax';
import { isProgramSOBEnabled } from '@/services/sobService';
import type { FormField } from '@/types/formSchema';
import { canWorkHumanAudit } from '@/utils/caseActions';
import { canAddNoteToCase, isRefillHumanAuditNeeded } from '@/utils/caseStateHelpers';
import { isSummaryEditableWorkflow } from '@/utils/workflow';

import { TimelineColumn } from './TimelineColumn';

export interface FieldGroup {
  label: string;
  fields: FormField[];
  summary?: string; // Optional summary text to display as subtitle
}

/**
 * Stable empty list for a case with no persisted disposition.
 *
 * `OutputBoardEditor` memoizes its filtered groups on the required/missing arrays, so a
 * fresh `[]` on every render would defeat that memo.
 */
const NO_DISPOSITION_FIELDS: string[] = [];

interface CaseSummaryViewProps {
  caseId: string;
  caseDetails: any;
  inputGroups: FieldGroup[];
  outputGroups: FieldGroup[];
  canEditInput: boolean;
  canEditOutput: boolean;
  isHumanAudit?: boolean; // Whether the case is in Human Audit stage
  workflowType?: string; // Workflow type (e.g., 'medbv', 'pharmbv', etc.)
  linkedCaseRecordIds?: string[]; // Linked case record IDs (for migration tracking)
  isNotesCollapsed?: boolean; // Controlled state for notes panel collapse
  onNotesCollapsedChange?: (collapsed: boolean) => void; // Callback when notes panel collapse state changes
  isTimelineCollapsed?: boolean; // Controlled state for timeline sidebar collapse
  onTimelineCollapsedChange?: (collapsed: boolean) => void; // Callback when timeline sidebar collapse state changes
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void; // Navigate to a specific call in Review tab
  onNavigateToAudit?: (auditId: string) => void; // Navigate to the AI Audit tab when the latest evidence is audit-sourced
}

export const CaseSummaryView: React.FC<CaseSummaryViewProps> = ({
  caseId,
  caseDetails,
  inputGroups,
  outputGroups,
  canEditInput,
  canEditOutput,
  isHumanAudit = false,
  workflowType,
  linkedCaseRecordIds,
  isNotesCollapsed: controlledIsNotesCollapsed,
  onNotesCollapsedChange,
  isTimelineCollapsed: controlledIsTimelineCollapsed,
  onTimelineCollapsedChange,
  onNavigateToCall,
  onNavigateToAudit,
}) => {
  // Notes sidebar collapse — controlled if a callback is provided, otherwise local state.
  const [localIsNotesCollapsed, setLocalIsNotesCollapsed] = useState(true);
  const isNotesCollapsed = controlledIsNotesCollapsed ?? localIsNotesCollapsed;
  const setIsNotesCollapsed = onNotesCollapsedChange ?? setLocalIsNotesCollapsed;
  // Timeline sidebar collapse — controlled if a callback is provided, otherwise local state.
  const [localIsTimelineCollapsed, setLocalIsTimelineCollapsed] = useState(true);
  const isTimelineCollapsed = controlledIsTimelineCollapsed ?? localIsTimelineCollapsed;
  const setIsTimelineCollapsed = onTimelineCollapsedChange ?? setLocalIsTimelineCollapsed;
  const [isTasksCollapsed, setIsTasksCollapsed] = useState(true);
  const showTasks = useStore(state => isMyTasksEnabledForOrg(state.selectedOrganizationId));
  const [editingSections, setEditingSections] = useState<{ input: boolean; output: boolean }>({
    input: false,
    output: false,
  });
  // Use controlled state if provided, otherwise use local stat
  const [editedSectionData, setEditedSectionData] = useState<{ input: any; output: any }>({
    input: null,
    output: null,
  });
  const { isRegenerating, regenerateSummary } = useRegenerateSummary(caseId, caseDetails);
  const { calculateDisposition } = useCalculateDisposition(caseId, caseDetails);
  const { notifyManualTaskStarted } = useManualTaskStarted(caseId);

  const { pdfBlobUrl, faxesData } = useFax();

  const [inputExpandedItems, setInputExpandedItems] = useState<string[]>(() =>
    createAllAccordionIndices(inputGroups.length),
  );
  const updateCaseInput = useUpdateCaseInput();
  const updateCaseOutput = useUpdateCaseOutput();
  const { selectedOrganizationId, selectedProgramId, getSelectedWorkflow } = useStore();
  const selectedWorkflow = getSelectedWorkflow();
  const isCurrentUserAssigned = useIsCurrentUserAssigned();
  const isTeamLead = useIsTeamLead();
  // A transportation or unifiedmedbv human audit is worked by Program Admins and above only
  // (BV-2657); for anyone else the read-only fields offer no "Accept" of an AI value.
  const isCaseProgramAdmin = useIsCaseProgramAdmin(
    caseDetails?.organization_id,
    caseDetails?.program_id,
  );
  const canWorkAudit = canWorkHumanAudit(caseDetails?.state, workflowType, isCaseProgramAdmin);
  const isAssigned =
    isCurrentUserAssigned(caseDetails?.assigned, caseDetails?.assigned_user_id) || isTeamLead;

  // BI-1246: workflows with the dedicated Human Audit tab (pbm/unifiedmedbv) do their
  // output + summary + disposition editing there. In those, hide those edit
  // affordances from the Summary view during human audit — only Input stays editable.
  const hideHumanAuditEditsInSummary =
    isHumanAudit &&
    (workflowType === 'pbm' ||
      workflowType === 'unifiedmedbv' ||
      workflowType === 'bho' ||
      workflowType === 'medbhobv' ||
      workflowType === 'pa_status_pbm' ||
      workflowType === 'pa_status_medbv');

  // Fetch the case summary (used for the summary-merged output groups below). The
  // disposition + full-summary editing lives in DispositionSummarySection (rendered
  // by OutputBoardEditor), which owns its own summary/checksum hooks.
  const { data: caseSummaryData } = useCaseSummary(caseId, !!caseId, {
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Construct output data structures
  // For case records, metadata is a sibling to data in CaseRecordData: { schema_version, data, metadata }
  const { outputData, outputMetadata } = React.useMemo(() => {
    const raw = caseDetails?.output_data?.data || caseDetails?.output_data;
    if (!raw) {
      return {
        outputData: constructEmptyDataFromGroups(outputGroups),
        outputMetadata: undefined as { fields?: Record<string, any> } | undefined,
      };
    }

    return {
      outputData: raw,
      outputMetadata: caseDetails?.output_data?.metadata as
        | { fields?: Record<string, any> }
        | undefined,
    };
  }, [caseDetails?.output_data, outputGroups]);

  // Enhance output groups with case summaries (not call summaries)
  const outputGroupsWithSummaries = React.useMemo(() => {
    // Extract summaries from the API response (now properly wrapped in 'summary' field)
    const summaries = caseSummaryData?.summary?.content?.section_summaries;

    // Debug logging
    console.debug('Case Summary Data:', {
      hasSummary: !!caseSummaryData?.summary,
      summaryId: caseSummaryData?.summary?.id,
      hasSectionSummaries: !!summaries,
      summaryKeys: summaries ? Object.keys(summaries) : [],
      groupLabels: outputGroups.map(g => g.label),
    });

    const enhancedGroups = outputGroups.map(group => ({
      ...group,
      summary: summaries?.[group.label] || undefined,
    }));

    return enhancedGroups;
  }, [outputGroups, caseSummaryData]);

  // Output editing (shared with the Human Audit tab via useOutputEditor). Validate
  // against the real output schema (outputGroups), not the summary-merged variant.
  // On output edit-start during human audit, emit the manual-task-started marker
  // (BI-913); after save, reconcile the persisted disposition.
  const outputEditor = useOutputEditor({
    caseId,
    validationGroups: outputGroups,
    baselineOutput: outputData,
    schemaVersion: '1.0',
    onEditStart: () => {
      if (isHumanAudit) notifyManualTaskStarted();
    },
    onSaved: () => calculateDisposition(isHumanAudit, workflowType),
  });

  // Disposition-driven filter for the Output column. Summary reads the PERSISTED
  // `caseDetails.disposition` — the API response — and deliberately does NOT run the
  // disposition engine (LA-6175). `fieldProps.dispositionFields` below has always used
  // the persisted disposition, so running the engine here made the All/Missed/Required
  // pills disagree with the per-section counters and the per-field icons on the same
  // board (engine said 27 required, the sections summed the persisted 25). One source
  // for every number, and it matches the disposition text the case actually carries.
  //
  // The live engine still drives the surfaces where an agent is actively filling fields:
  // the Review tab's human-call output and the Human Audit tab, both via
  // `useCommittedDisposition`.
  //
  // Consequence: these lists are eventually consistent. They change only when something
  // recomputes the persisted disposition — a hermes workflow step, or the
  // ACTION_CALCULATE_DISPOSITION fired on a human-audit output save (`useSummary`) — and
  // land in the UI when the case-record SSE watch invalidates the query. A case whose
  // CASE-level disposition was never written (the pa_status pair dispose per CALL) shows
  // no filter until its first human-audit save.
  const requiredFields = caseDetails?.disposition?.required_fields ?? NO_DISPOSITION_FIELDS;
  const missingFields = caseDetails?.disposition?.missing_fields ?? NO_DISPOSITION_FIELDS;

  // Whether to show regenerate summary controls
  const canRegenerateSummary =
    canEditOutput &&
    isAssigned &&
    isSummaryEditableWorkflow(workflowType) &&
    isHumanAudit &&
    !hideHumanAuditEditsInSummary &&
    !outputEditor.isEditing &&
    !isRegenerating;

  // State for confirmation dialog when accepting extracted values
  const [acceptConfirmation, setAcceptConfirmation] = useState<{
    fieldPath: string;
    value: any;
    fieldLabel: string;
  } | null>(null);

  const handleAcceptExtracted = useCallback((fieldPath: string, value: any, fieldLabel: string) => {
    setAcceptConfirmation({ fieldPath, value, fieldLabel });
  }, []);

  const confirmAcceptExtracted = useCallback(async () => {
    if (!acceptConfirmation || !caseId) return;

    const { fieldPath, value } = acceptConfirmation;

    // Build updated output data
    const currentData = caseDetails?.output_data?.data || {};
    const updatedData = { ...currentData };

    // Set nested value (handles dot notation paths)
    const keys = fieldPath.split('.');
    let obj: any = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      obj[keys[i]] = obj[keys[i]] ? { ...obj[keys[i]] } : {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;

    try {
      await new Promise((resolve, reject) => {
        updateCaseOutput.mutate(
          { caseId, outputData: { schema_version: '1.0', data: updatedData } },
          { onSuccess: resolve, onError: reject },
        );
      });
      toast.success('Accepted extracted value');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to accept value');
    } finally {
      setAcceptConfirmation(null);
    }
  }, [acceptConfirmation, caseId, caseDetails, updateCaseOutput]);

  // Initialize input accordion expanded items when groups change (output board
  // owns its own expansion state inside OutputBoardEditor).
  React.useEffect(() => {
    setInputExpandedItems(inputGroups.map((_, index) => index.toString()));
  }, [inputGroups.length]); // Only depend on length to avoid infinite loops

  // Input section edit start. (Output editing lives in `outputEditor`.)
  const handleInputEditStart = () => {
    setEditingSections(prev => ({ ...prev, input: true }));
    setEditedSectionData(prev => ({
      ...prev,
      input: JSON.parse(JSON.stringify(inputData || {})),
    }));
    // Fire the manual-task-started marker (hermes PR #820 / BI-913) — input edit
    // only counts on the missing-info step.
    if (caseDetails?.state === CaseState.HUMAN_STEP_MISSING_INFO) {
      notifyManualTaskStarted();
    }
  };

  // Input section edit cancel.
  const handleInputEditCancel = () => {
    setEditingSections(prev => ({ ...prev, input: false }));
    setEditedSectionData(prev => ({ ...prev, input: null }));
  };

  // Handle accordion toggle for input
  const handleInputToggleAll = () => {
    const newInputExpandedItems = toggleAllAccordionItems(inputExpandedItems, inputGroups.length);
    setInputExpandedItems(newInputExpandedItems);
  };

  // Save the input section. (Output saves through `outputEditor`.)
  const handleInputSave = async () => {
    if (!caseId || !caseDetails) return;
    const updatedData = editedSectionData.input;
    if (!updatedData) return;

    try {
      await new Promise((resolve, reject) => {
        updateCaseInput.mutate(
          {
            caseId,
            inputData: {
              schema_version: '1.0',
              data: normalizePhoneFieldsForSave(
                inputGroups,
                coerceFormDataToSchema(updatedData, inputGroups),
              ),
            },
          },
          { onSuccess: resolve, onError: reject },
        );
      });
      setEditingSections(prev => ({ ...prev, input: false }));
      setEditedSectionData(prev => ({ ...prev, input: null }));
      toast.success('Input data updated successfully');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to update input data';
      toast.error(errorMessage);
    }
  };

  // Determine if we should show SOB preview (3 columns) or just input/output (2 columns)
  // All columns use flex to share available space proportionally
  const showSOBPreview = !!caseDetails?.program_id && isProgramSOBEnabled(caseDetails.program_id);

  // Check if we should show fax-related UI (FaxDocViewer and extracted data)
  const isReferralScheduler = selectedWorkflow?.workflow_type === 'referral_scheduler';
  // Merge fax data with input data
  const inputData = React.useMemo(() => {
    const baseInputData = caseDetails?.input_data?.data || caseDetails?.input_data || {};

    // If we have fax data, merge it with input data
    if (isReferralScheduler && faxesData?.faxes?.[0]?.output_data) {
      return {
        ...baseInputData,
        ...faxesData.faxes[0].output_data,
      };
    }

    return baseInputData;
  }, [caseDetails?.input_data, faxesData, isReferralScheduler]);

  const renderInputColumn = () => {
    return (
      <div className="flex-1 min-w-0 border-r flex flex-col">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <h4 className="text-muted-foreground font-medium text-sm flex items-center gap-2">
              Input
              {caseDetails?.error && <CircleAlert className="h-4 w-4 text-destructive" />}
            </h4>
            <div className="flex items-center gap-1">
              {canEditInput && isAssigned && (
                <div className="flex items-center gap-1">
                  {editingSections.input ? (
                    <>
                      <SimpleTooltip content="Save" side="bottom">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleInputSave}
                          disabled={updateCaseInput.isPending}
                          className="h-5 w-5 p-0"
                          data-testid="save-input-button"
                        >
                          <Save className="w-3 h-3" />
                        </Button>
                      </SimpleTooltip>
                      <SimpleTooltip content="Discard" side="bottom">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleInputEditCancel}
                          disabled={updateCaseInput.isPending}
                          className="h-5 w-5 p-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </SimpleTooltip>
                    </>
                  ) : (
                    <SimpleTooltip content="Edit" side="bottom">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleInputEditStart}
                        className="h-5 w-5 p-0"
                        data-testid="edit-input-button"
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    </SimpleTooltip>
                  )}
                </div>
              )}
              {/* Expand/Collapse All button for Input */}
              <SimpleTooltip
                content={
                  inputExpandedItems.length === inputGroups.length ? 'Collapse All' : 'Expand All'
                }
                side="bottom"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInputToggleAll}
                  className="h-5 w-5 p-0"
                >
                  <ChevronsDownUp className="w-3 h-3" />
                </Button>
              </SimpleTooltip>
            </div>
          </div>
          {caseDetails?.error && (
            <Alert variant="destructive" density="compact" className=" mt-2">
              <AlertDescription className="whitespace-pre-line text-destructive max-h-32 overflow-y-auto w-full">
                {formatErrorMessage(caseDetails.error, inputGroups)}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-2 space-y-3">
          {/* Extracted Fax Data Accordion */}

          <FieldGroupAccordion
            groups={inputGroups}
            data={editingSections.input ? editedSectionData.input : inputData}
            editableState={null}
            isEditable={editingSections.input}
            section="input"
            caseRecordId={caseId}
            onDataChange={updatedData =>
              setEditedSectionData(prev => ({ ...prev, input: updatedData }))
            }
            showCollapseAll={false}
            externalExpandedItems={inputExpandedItems}
            onExpandedItemsChange={setInputExpandedItems}
            defaultExpanded={true}
            emptyStateMessage={{
              title: 'No Request Data Available',
              subtitle: 'Request data will appear here',
            }}
            workflowType={workflowType}
          />
        </div>
      </div>
    );
  };

  const renderOutputColumn = () => {
    return (
      <div className={`flex-1 min-w-0 ${showSOBPreview ? 'border-r' : ''} flex flex-col`}>
        <div className="flex-1 overflow-y-auto px-4 py-2 pb-2 space-y-3">
          {/* Shared output board (also used by the Human Audit tab), including the
              disposition + summary section it now renders below the Output header. The
              Summary view uses the PERSISTED disposition (`caseDetails.disposition`) for
              the filter pills, the section counters AND the per-field icons — the same
              source throughout, see the requiredFields/missingFields comment above — and
              renders the summary-merged groups. */}
          <OutputBoardEditor
            editor={outputEditor}
            // The output editor shows in hermes' human_audit and in refill's
            // human_audit_needed (JNJ-172); canEditOutput is still the permission.
            canEdit={
              canEditOutput &&
              isAssigned &&
              (isHumanAudit || isRefillHumanAuditNeeded(workflowType, caseDetails?.state)) &&
              !hideHumanAuditEditsInSummary
            }
            groups={outputGroupsWithSummaries}
            requiredFields={requiredFields}
            missingFields={missingFields}
            isRegenerating={isRegenerating}
            caseId={caseId}
            caseDetails={caseDetails}
            canEditDisposition={
              canEditOutput &&
              isAssigned &&
              isHumanAudit &&
              !hideHumanAuditEditsInSummary &&
              !isRegenerating
            }
            editTestId="edit-output-button"
            saveTestId="save-output-button"
            allEmptyStateMessage={{
              title: 'No Summary Data Available',
              subtitle: 'Summary data will appear here once processed',
            }}
            headerActions={
              <>
                {canRegenerateSummary && (
                  <SimpleTooltip content="Regenerate Summary" side="bottom">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => regenerateSummary(isHumanAudit, workflowType)}
                      className="h-6 w-6 p-0"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </SimpleTooltip>
                )}
                {isRegenerating && (
                  <SimpleTooltip content="Regenerating summary..." side="bottom">
                    <Loader className="w-3 h-3 animate-spin text-muted-foreground" />
                  </SimpleTooltip>
                )}
                {workflowType === 'medicare_human' &&
                  selectedOrganizationId &&
                  selectedProgramId && (
                    <MoveToMedBV
                      caseId={caseId}
                      linkedCaseRecordIds={linkedCaseRecordIds}
                      organizationId={selectedOrganizationId}
                      programId={selectedProgramId}
                    />
                  )}
              </>
            }
            fieldProps={{
              extractedData: caseDetails?.extracted_data?.output?.data,
              editableState: null,
              canEditSummaries: false,
              defaultExpanded: true,
              workflowType,
              isHumanAudit: caseDetails?.state === CaseState.HUMAN_AUDIT,
              onAcceptExtracted: canWorkAudit ? handleAcceptExtracted : undefined,
              extractedMetadata: caseDetails?.extracted_data?.output?.metadata,
              outputMetadata,
              onNavigateToCall,
              onNavigateToAudit,
              dispositionFields: caseDetails?.disposition,
            }}
          />
        </div>
      </div>
    );
  };

  // renderInputColumn and renderOutputColumn are called as functions (not rendered as
  // components) to ensure React sees stable element types during reconciliation.
  // Using <InputColumn /> syntax with inline functions causes unmount/remount on every render.

  return (
    <div className="flex flex-1 min-h-0">
      {isReferralScheduler ? (
        <div className="flex-1 min-w-0">
          <FaxSummaryView
            pdfUrl={pdfBlobUrl}
            InputColumn={renderInputColumn()}
            OutputColumn={renderOutputColumn()}
          />
        </div>
      ) : (
        <div className="flex flex-1">
          {/* Column 1 - Input Data */}
          {renderInputColumn()}
          {/* Column 2 - Output Data */}
          {renderOutputColumn()}
        </div>
      )}

      {/* Column 3 - SOB Preview (conditional) */}
      {showSOBPreview && caseDetails?.program_id && (
        <div className="flex-[1.3] min-w-0 border-r flex flex-col">
          <SOBPreview
            caseData={caseDetails}
            programId={caseDetails.program_id}
            summary={
              caseSummaryData?.summary?.content?.section_summaries
                ? { section_summaries: caseSummaryData.summary.content.section_summaries }
                : undefined
            }
          />
        </div>
      )}
      {/* Right-side collapsible tasks column, first of Tasks / Notes / Timeline (same org gate as My Tasks) */}
      {showTasks && (
        <TasksColumn
          caseRecordId={caseId}
          isCollapsed={isTasksCollapsed}
          onCollapsedChange={setIsTasksCollapsed}
        />
      )}

      {/* Right-side collapsible notes column */}
      <NotesColumn
        caseRecordId={caseId}
        canAddNote={canAddNoteToCase(caseDetails?.state)}
        isCollapsed={isNotesCollapsed}
        onCollapsedChange={setIsNotesCollapsed}
      />

      {/* Right-side collapsible activity timeline column */}
      <TimelineColumn
        caseRecordId={caseId}
        isCollapsed={isTimelineCollapsed}
        onCollapsedChange={setIsTimelineCollapsed}
      />

      {/* Confirmation Dialog for Accepting Extracted Values */}
      <Dialog open={!!acceptConfirmation} onOpenChange={() => setAcceptConfirmation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Extracted Value?</DialogTitle>
            <DialogDescription>
              This will update &quot;{acceptConfirmation?.fieldLabel}&quot; to &quot;
              {typeof acceptConfirmation?.value === 'object'
                ? JSON.stringify(acceptConfirmation?.value)
                : String(acceptConfirmation?.value ?? '')}
              &quot;. You can edit this value later, but there&apos;s no one-click undo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptConfirmation(null)}>
              Cancel
            </Button>
            <Button onClick={confirmAcceptExtracted}>Accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
